import { useState } from 'react';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import useLogout from 'hooks/ui/useLogout';
import { useHistory } from 'react-router-dom';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useGetAllPermissionsList } from 'hooks/ui/usePermissions';
import { itemsContainUrl } from 'helpers/layout/sidebarMenu';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { RolePermission } from 'further-types/admin-user-role';

const NavigationElement = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'nestingLevel',
})<{ nestingLevel: number; selected: boolean }>(
  ({ theme, nestingLevel, selected }) => ({
    padding: theme.spacing(
      theme.spacingSizes.xs,
      2,
      theme.spacingSizes.xs,
      theme.spacingSizes.sm + nestingLevel * 4,
    ),
    border: `1px solid ${
      selected ? theme.palette.borderColor.main : 'transparent'
    }`,
  }),
);

const ExpandLessIcon = styled(ExpandLess)(({ theme }) => ({
  color: theme.palette.grey[900],
}));

const NavigationItemIcon = styled(ListItemIcon)({
  minWidth: '20px',
  width: '20px',

  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
});

type NavigationItemType = {
  name: string;
  icon?: React.ReactNode;
  type: string;
  link?: string;
  isLogout?: boolean;
  onClick?: () => void;
  children?: Array<NavigationItemType>;
  onlyShownForRoles?: Array<string>;
  hiddenForRoles?: Array<string>;
  permission?: RolePermission;
  anyOfPermissions?: Array<RolePermission>;
};

type Props = NavigationItemType & {
  nestingLevel?: number;
};

const NavigationItem: React.FC<Props> = ({
  name,
  icon,
  type,
  children,
  onClick,
  link,
  isLogout,
  onlyShownForRoles,
  hiddenForRoles,
  permission,
  anyOfPermissions,
  nestingLevel = 0,
}) => {
  const history = useHistory();
  const { roleSlug } = useGetRole();
  const [open, setOpen] = useState(() =>
    itemsContainUrl(children ?? [], history.location.pathname),
  );
  const { handleLogout } = useLogout();
  const permissionsList = useGetAllPermissionsList();

  const isPermittedForRole = onlyShownForRoles?.length
    ? onlyShownForRoles.includes(roleSlug ?? '')
    : true;

  const isHiddenForRole = hiddenForRoles?.length
    ? hiddenForRoles.includes(roleSlug ?? '')
    : false;

  const permissionOk = anyOfPermissions?.length
    ? anyOfPermissions.some((p) => permissionsList?.includes(p))
    : permission
    ? permissionsList?.includes(permission)
    : true;

  if (!isPermittedForRole || isHiddenForRole || !permissionOk) {
    return null;
  }

  const isSelected = !!link && history.location.pathname === link;

  const handleClick = (e: React.MouseEvent) => {
    if (type === 'collapse') {
      setOpen(!open);
    }
    if (isLogout) {
      e.preventDefault();
      handleLogout();
    } else if (link) {
      if (!e.metaKey && !e.ctrlKey && e.button !== 1) {
        e.preventDefault();
        history.push(link);
      }
    } else {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <>
      <NavigationElement
        {...(link ? { component: 'a', href: link } : {})}
        selected={isSelected}
        nestingLevel={nestingLevel}
        dense
        onClick={handleClick}
      >
        {icon ? <NavigationItemIcon>{icon}</NavigationItemIcon> : null}
        <ListItemText
          slotProps={{
            primary: {
              fontWeight: 'medium',
              color: isSelected || open ? 'grey.900' : 'grey.600',
              lineHeight: '150%',
            },
          }}
          primary={name}
        />
        {type === 'collapse' ? (
          open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )
        ) : null}
      </NavigationElement>
      {type === 'collapse' && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children?.map((child) => (
              <NavigationItem
                key={child.name}
                {...child}
                nestingLevel={nestingLevel + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default NavigationItem;
